import type { ProceduralBeat } from "@engine";
import type { SegmentFactory } from "../level/SegmentFactory";

// Resting emissive intensity for the pulse-base materials between
// kicks. Matches the regular base material so non-pulse segments and
// the calm zones of pulse segments read identically.
const REST_INTENSITY = 0.18;
// Floor the pulse-base material drops to on each kick. Low enough to
// effectively black out the wall against the sky tunnel for ~50 ms.
const PULSE_FLOOR = 0.04;
// How long the lerp from PULSE_FLOOR back to REST_INTENSITY takes. At
// 140 BPM half-time, kicks are ~857 ms apart so this leaves the
// majority of the bar with the wall fully visible -- the dip feels
// like a strobe rather than a permanent dim.
const RECOVERY_S = 0.2;

// Subscribes to the kick beat and dives the per-shape pulse-base
// material's emissive intensity to PULSE_FLOOR, then linearly recovers
// to REST_INTENSITY over RECOVERY_S. Pulse-trick segments use that
// shared material (see SegmentFactory.getPulseBaseMaterialByShape) so
// every panel of every active pulse segment dims in unison without us
// touching individual meshes.
//
// Mechanic: the wall is briefly dark right when the bass hits, so
// players have to read the gap layout in advance (or trust the lit
// floor tile under them).
export class PulsePanelsFx {
  private factory: SegmentFactory;
  private elapsedSinceKick = Infinity;
  private unsub: (() => void) | null = null;

  constructor(factory: SegmentFactory, beat: ProceduralBeat) {
    this.factory = factory;
    this.unsub = beat.onStep.on((step) => {
      if (!step.isKick) return;
      // Reset on every kick so a snare-roll bar (which has consecutive
      // kicks at the start of the next bar) still produces visible
      // strobing instead of holding at the floor.
      this.elapsedSinceKick = 0;
    });
  }

  // Driven on UNSCALED dt so a slow-mo pickup doesn't slow the
  // strobe -- the kick ticks at audio time regardless.
  update(unscaledDt: number): void {
    if (this.elapsedSinceKick >= RECOVERY_S) {
      this.applyToAll(REST_INTENSITY);
      return;
    }
    this.elapsedSinceKick += unscaledDt;
    const t = Math.min(1, this.elapsedSinceKick / RECOVERY_S);
    const intensity = PULSE_FLOOR + (REST_INTENSITY - PULSE_FLOOR) * t;
    this.applyToAll(intensity);
  }

  // External reset (game state change, level switch). Snaps materials
  // back to the resting value so the next pulse segment doesn't load
  // mid-strobe.
  reset(): void {
    this.elapsedSinceKick = Infinity;
    this.applyToAll(REST_INTENSITY);
  }

  dispose(): void {
    if (this.unsub) {
      this.unsub();
      this.unsub = null;
    }
  }

  private applyToAll(intensity: number): void {
    const mats = this.factory.getPulseMaterials();
    for (let i = 0; i < mats.length; i++) {
      mats[i]!.emissiveIntensity = intensity;
    }
  }
}
